import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// กลุ่มงานตามสถานะ (key ตรงกับ GROUPS / getStatusGroup ในหน้า dashboard)
export const STATUS_GROUPS = [
  { key: "prep", label: "สืบทรัพย์ - ส่งบอกเลิก", color: "FFFAAD14" },
  { key: "prepLawsuit", label: "เตรียมฟ้อง - เอกสาร", color: "FF722ED1" },
  { key: "court", label: "ดำเนินชั้นศาล - พิพากษา", color: "FFF5222D" },
  { key: "review", label: "ตรวจสอบ - สัญญาใหม่", color: "FF52C41A" },
  { key: "other", label: "อื่น ๆ", color: "FF8C8C8C" },
];

const DEBT_NCSHPRC_TYPES = ["rpsl", "rpsl-h"];

export const getDebt = (db2) => {
  if (!db2) return null;
  return DEBT_NCSHPRC_TYPES.includes(db2.LOAN?.DATA_TYPE)
    ? db2.LOAN?.NCSHPRC
    : db2.LOAN?.TOTPRC;
};

// ยอดหนี้คงเหลือ = ยอดหนี้ - จ่ายมาทั้งหมด (SMPAY)
export const getRemainDebt = (db2) => {
  const debt = getDebt(db2);
  if (debt === null || debt === undefined || debt === "") return null;
  const remain = Number(debt) - Number(db2.LOAN?.SMPAY || 0);
  return isNaN(remain) ? null : Math.round(remain * 100) / 100;
};

export const getCustomerName = (item) =>
  `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`.trim();

// เพิ่มข้อมูลที่ excel ใช้ (item ต้องมี db2 จาก mergeDB2IntoDetail แล้ว)
export const enrichCase = (item, getTaskStatus) => {
  const expenses = Array.isArray(item.EXPENSE_LIST) ? item.EXPENSE_LIST : [];
  return {
    ...item,
    status: getTaskStatus(item),
    expenseTotal: expenses.reduce(
      (sum, e) => sum + (parseFloat(e.PAY ?? e.NETPAY) || 0),
      0,
    ),
  };
};

// ---------- Excel ----------

const toNumber = (value) =>
  value === null || value === undefined || value === "" || isNaN(Number(value))
    ? null
    : Number(value);

const MONEY_FMT = "#,##0.00";

// showLawyer: แสดงคอลัมน์ ผู้รับผิดชอบ (ใช้ตอนไม่ได้เลือกทนาย)
const buildColumns = (formatDate, showLawyer) => {
  const date = (v) => (v ? formatDate(v) : "-");
  const columns = [
    { header: "ลำดับ", width: 7, value: (r, i) => i + 1 },
    { header: "เลขสัญญา", width: 14, value: (r) => r.CONTNO || "-" },
    {
      header: "เลขสัญญาใหม่",
      width: 14,
      value: (r) => r.newContno || "-",
    },
    {
      header: "ชื่อลูกค้า",
      width: 30,
      value: (r) => getCustomerName(r) || "-",
    },
    {
      key: "NNAME",
      header: "ผู้รับผิดชอบ",
      width: 16,
      value: (r) => r.NNAME || "-",
    },
    { header: "สาขา", width: 8, value: (r) => r.db2?.LOAN?.LOCAT || "-" },
    { header: "สถานะล่าสุด", width: 20, value: (r) => r.status || "-" },
    {
      group: "ฟ้อง",
      header: "ศาล",
      width: 22,
      value: (r) => r.provincialCourt || "-",
    },
    {
      group: "ฟ้อง",
      header: "คดีดำ",
      width: 16,
      value: (r) => r.blackCaseNumber || "-",
    },
    {
      group: "ฟ้อง",
      header: "วันนัดศาล",
      width: 13,
      value: (r) => date(r.considerationDate),
    },
    {
      group: "ฟ้อง",
      header: "ทุนทรัพย์ฟ้อง",
      width: 15,
      money: true,
      value: (r) => toNumber(r.litigationFunds),
    },
    {
      group: "ฟ้อง",
      header: "ค่าใช้จ่ายในคดี",
      width: 15,
      money: true,
      value: (r) => (r.EXPENSE_LIST?.length ? r.expenseTotal : null),
    },
    {
      group: "ชั้นศาล",
      header: "คดีแดง",
      width: 16,
      value: (r) => r.redCaseNumber || "-",
    },
    {
      group: "ชั้นศาล",
      header: "ยอดพิพากษา",
      width: 15,
      money: true,
      value: (r) => toNumber(r.totalJugde),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "ยอดหนี้",
      width: 15,
      money: true,
      value: (r) => toNumber(getDebt(r.db2)),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "จ่ายมาทั้งหมด",
      width: 15,
      money: true,
      value: (r) => (r.db2 ? toNumber(r.db2.LOAN?.SMPAY) : null),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "ยอดหนี้คงเหลือ",
      width: 15,
      money: true,
      value: (r) => getRemainDebt(r.db2),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "จ่ายล่าสุด",
      width: 13,
      value: (r) => date(r.db2?.LOAN?.LPAYD),
    },
  ];
  return showLawyer ? columns : columns.filter((c) => c.key !== "NNAME");
};

// ยอดรวมที่ใช้ใน sheet สรุป (สรุปตามงาน / สรุปตามทนาย)
const SUMMARY_METRICS = [
  { header: "ทุนทรัพย์ฟ้อง", value: (r) => r.litigationFunds },
  { header: "ค่าใช้จ่ายในคดี", value: (r) => r.expenseTotal },
  { header: "ยอดพิพากษา", value: (r) => r.totalJugde },
  { header: "ยอดหนี้คงเหลือ", value: (r) => getRemainDebt(r.db2) },
];

const sum = (rows, fn) => rows.reduce((s, r) => s + (toNumber(fn(r)) || 0), 0);

const thinBorder = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

const fill = (argb) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb },
});

// ชื่อ sheet ห้ามมี \ / ? * [ ] : และยาวไม่เกิน 31 ตัว
const safeSheetName = (name) => name.replace(/[\\/?*[\]:]/g, "-").slice(0, 31);

const addCaseSheet = (workbook, sheet, columns) => {
  const { name, color, rows, subtitle } = sheet;
  const headerOf = (col) =>
    typeof col.header === "function" ? col.header(sheet) : col.header;
  const ws = workbook.addWorksheet(safeSheetName(name), {
    views: [{ state: "frozen", xSplit: 2, ySplit: 4 }],
  });
  const lastCol = columns.length;
  ws.columns = columns.map((c) => ({ width: c.width }));

  // หัวรายงาน
  ws.mergeCells(1, 1, 1, lastCol);
  const title = ws.getCell(1, 1);
  title.value = `${name} (${rows.length} สัญญา)`;
  title.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
  title.fill = fill(color);
  title.alignment = { vertical: "middle", horizontal: "left" };
  ws.getRow(1).height = 24;

  ws.mergeCells(2, 1, 2, lastCol);
  ws.getCell(2, 1).value = subtitle || "";
  ws.getCell(2, 1).font = { italic: true, color: { argb: "FF595959" } };

  // หัวตาราง 2 แถว (กลุ่ม / คอลัมน์)
  const groupRow = 3;
  const headRow = 4;
  let i = 0;
  while (i < columns.length) {
    const col = columns[i];
    if (!col.group) {
      ws.mergeCells(groupRow, i + 1, headRow, i + 1);
      ws.getCell(groupRow, i + 1).value = headerOf(col);
      i += 1;
      continue;
    }
    let j = i;
    while (j + 1 < columns.length && columns[j + 1].group === col.group) j += 1;
    ws.mergeCells(groupRow, i + 1, groupRow, j + 1);
    ws.getCell(groupRow, i + 1).value = col.group;
    for (let k = i; k <= j; k += 1) {
      ws.getCell(headRow, k + 1).value = headerOf(columns[k]);
    }
    i = j + 1;
  }
  [groupRow, headRow].forEach((r) => {
    for (let c = 1; c <= lastCol; c += 1) {
      const cell = ws.getCell(r, c);
      cell.font = { bold: true };
      cell.fill = fill("FFE8EDF5");
      cell.border = thinBorder;
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
    }
  });

  // ข้อมูล
  rows.forEach((record, index) => {
    const row = ws.addRow(columns.map((c) => c.value(record, index) ?? "-"));
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const col = columns[colNumber - 1];
      cell.border = thinBorder;
      const isNumber = typeof cell.value === "number";
      if (col.money && isNumber) cell.numFmt = MONEY_FMT;
      cell.alignment = {
        vertical: "top",
        horizontal: isNumber ? "right" : undefined,
        wrapText: true,
      };
      if (index % 2 === 1) cell.fill = fill("FFFAFAFA");
    });
  });

  // แถวรวม (เฉพาะคอลัมน์เงิน)
  if (rows.length > 0) {
    const firstData = headRow + 1;
    const lastData = headRow + rows.length;
    const total = ws.addRow([]);
    total.getCell(1).value = "รวม";
    ws.mergeCells(total.number, 1, total.number, 3);
    columns.forEach((c, idx) => {
      if (!c.money) return;
      const letter = ws.getColumn(idx + 1).letter;
      total.getCell(idx + 1).value = {
        formula: `SUM(${letter}${firstData}:${letter}${lastData})`,
      };
      total.getCell(idx + 1).numFmt = MONEY_FMT;
    });
    total.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { bold: true };
      cell.fill = fill("FFFFF7E6");
      cell.border = thinBorder;
    });
  }
};

// แถวรวมด้วยสูตร SUM ตั้งแต่ firstData ถึงแถวก่อนหน้า
const addTotalRow = (ws, firstData, lastCol, moneyFromCol) => {
  const lastData = ws.lastRow.number;
  const total = ws.addRow(["รวม"]);
  for (let c = 2; c <= lastCol; c += 1) {
    const letter = ws.getColumn(c).letter;
    total.getCell(c).value = {
      formula: `SUM(${letter}${firstData}:${letter}${lastData})`,
    };
  }
  total.font = { bold: true };

  for (let r = firstData; r <= total.number; r += 1) {
    for (let c = 1; c <= lastCol; c += 1) {
      const cell = ws.getRow(r).getCell(c);
      cell.border = thinBorder;
      if (c >= moneyFromCol) cell.numFmt = MONEY_FMT;
    }
  }
  total.eachCell((cell) => (cell.fill = fill("FFFFF7E6")));
};

// ตารางสรุปแยกตามทนาย ต่อจากสรุปตามงาน (ใช้ตอนไม่ได้เลือกทนาย)
const addLawyerSummary = (ws, sheets) => {
  const lawyerOf = (r) => r.NNAME || "ยังไม่มอบหมาย";
  const lawyerNames = [
    ...new Set(sheets.flatMap((sheet) => sheet.rows.map(lawyerOf))),
  ].sort((a, b) => a.localeCompare(b, "th"));
  if (lawyerNames.length === 0) return;

  const countCols = sheets.length + 1; // จำนวนแต่ละงาน + รวมสัญญา
  const lastCol = 1 + countCols + SUMMARY_METRICS.length;
  for (let c = 2; c <= lastCol; c += 1) {
    ws.getColumn(c).width = Math.max(ws.getColumn(c).width || 0, 16);
  }

  ws.addRow([]);
  const titleRow = ws.addRow(["สรุปตามทนาย"]);
  ws.mergeCells(titleRow.number, 1, titleRow.number, lastCol);
  titleRow.getCell(1).font = { bold: true, size: 14 };

  const header = ws.addRow([
    "ทนาย",
    ...sheets.map((sheet) => sheet.name),
    "รวมสัญญา",
    ...SUMMARY_METRICS.map((m) => m.header),
  ]);
  header.eachCell((cell, col) => {
    const sheet = sheets[col - 2];
    cell.font = { bold: true, color: sheet ? { argb: "FFFFFFFF" } : undefined };
    cell.fill = fill(sheet ? sheet.color : "FFE8EDF5");
    cell.border = thinBorder;
    cell.alignment = { horizontal: "center", wrapText: true };
  });

  const allRows = sheets.flatMap((sheet) => sheet.rows);
  const firstData = header.number + 1;
  lawyerNames.forEach((name) => {
    const rows = allRows.filter((r) => lawyerOf(r) === name);
    ws.addRow([
      name,
      ...sheets.map(
        (sheet) => sheet.rows.filter((r) => lawyerOf(r) === name).length,
      ),
      rows.length,
      ...SUMMARY_METRICS.map((m) => sum(rows, m.value)),
    ]);
  });
  addTotalRow(ws, firstData, lastCol, 2 + countCols);
};

const addSummarySheet = (workbook, sheets, subtitle, showLawyer) => {
  const ws = workbook.addWorksheet("สรุป");
  const lastCol = 2 + SUMMARY_METRICS.length;
  ws.columns = [
    { width: 24 },
    { width: 12 },
    ...SUMMARY_METRICS.map(() => ({ width: 18 })),
  ];
  ws.mergeCells(1, 1, 1, lastCol);
  ws.getCell("A1").value = "สรุปตามงาน";
  ws.getCell("A1").font = { bold: true, size: 14 };
  ws.mergeCells(2, 1, 2, lastCol);
  ws.getCell("A2").value = subtitle || "";
  ws.getCell("A2").font = { italic: true, color: { argb: "FF595959" } };

  const header = ws.addRow([
    "งาน",
    "จำนวนสัญญา",
    ...SUMMARY_METRICS.map((m) => m.header),
  ]);
  header.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = fill("FFE8EDF5");
    cell.border = thinBorder;
    cell.alignment = { horizontal: "center" };
  });

  const firstData = header.number + 1;
  sheets.forEach((sheet) => {
    const row = ws.addRow([
      sheet.name,
      sheet.rows.length,
      ...SUMMARY_METRICS.map((m) => sum(sheet.rows, m.value)),
    ]);
    row.getCell(1).fill = fill(sheet.color);
    row.getCell(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  });
  addTotalRow(ws, firstData, lastCol, 3);

  if (showLawyer) addLawyerSummary(ws, sheets);
};

/**
 * export excel รายงานสรุปผล แยก sheet ตามงาน (STATUS_GROUPS) + sheet สรุป
 * rows: enrichCase[]
 * groupOf: (status, item) => key ของ STATUS_GROUPS
 */
export const exportSummaryResultExcel = async ({
  rows,
  fileName,
  subtitle,
  formatDate,
  groupOf,
  showLawyer = false,
}) => {
  const workbook = new ExcelJS.Workbook();
  const columns = buildColumns(formatDate, showLawyer);
  const sheets = STATUS_GROUPS.map((group) => ({
    name: group.label,
    color: group.color,
    rows: rows.filter((r) => groupOf(r.status, r) === group.key),
  }));

  addSummarySheet(workbook, sheets, subtitle, showLawyer);
  sheets
    .filter((sheet) => sheet.rows.length > 0)
    .forEach((sheet) =>
      addCaseSheet(workbook, { ...sheet, subtitle }, columns),
    );

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    fileName,
  );
};
